import { CAPABILITIES } from './universes';
import { proposalAmbition, proposalSections } from './proposal-content';
import { MAX_BRIEF_MESSAGE, briefFilename } from './elina-brief';
import { validateReferences, type ElinaReference } from './elina-reference';

export { ELINA_SESSION_KEY } from './elina-entry';
export const ELINA_PROMPT_VERSION = 'public-interview-2026-10-12-references-v5';
export interface ProjectBenefit { capabilityId: string; title: string; description: string }
export interface ProjectStep { title: string; description: string }
export interface ProjectSolution {
  id: string; title: string; description: string; value: string;
  status: 'explore' | 'retained'; usesAi: boolean; capabilityIds: string[];
}
export interface ElinaProject {
  title: string; situation: string; primaryGoal: string; secondaryGoal: string;
  tertiaryGoal: string; ambition: string; ecosystem: string; solutions: ProjectSolution[];
  recommendation: string; businessBenefits: ProjectStep[];
  activity: string; team: string; tools: string; timing: string;
  benefits: ProjectBenefit[]; journey: ProjectStep[]; assumptions: string[];
}
export interface ProjectProposal extends ElinaProject { version: 1 | 2; source: 'elina' }
export interface ElinaReply {
  references?: ElinaReference[];
  heading: string; message: string; question: string; choices: string[];
  choiceMode: 'single' | 'ranked';
  ready: boolean; project: ElinaProject;
}
export interface InterviewMessage { role: 'user' | 'assistant'; text: string; briefName?: string }
export function validateInterviewMessage(value: unknown): InterviewMessage {
  if (!value || typeof value !== 'object') throw new Error('INVALID_INPUT');
  const v = value as Record<string, unknown>;
  const hasBrief = v.briefName !== undefined;
  if (hasBrief && (v.role !== 'user' || typeof v.briefName !== 'string' || !v.briefName.trim() || v.briefName.length > 120 || briefFilename(v.briefName) !== v.briefName)) throw new Error('INVALID_INPUT');
  if (!['user', 'assistant'].includes(String(v.role)) || typeof v.text !== 'string' || !v.text.trim() || v.text.length > (hasBrief ? MAX_BRIEF_MESSAGE : 1600)) throw new Error('INVALID_INPUT');
  return { role: v.role as InterviewMessage['role'], text: v.text.trim(), ...(hasBrief ? { briefName: v.briefName as string } : {}) };
}
export function emptyElinaProject(): ElinaProject {
  return { title: '', situation: '', primaryGoal: '', secondaryGoal: '', tertiaryGoal: '', ambition: '', ecosystem: '', recommendation: '', businessBenefits: [], solutions: [], activity: '', team: '', tools: '', timing: '', benefits: [], journey: [], assumptions: [] };
}
export const isSolutionId = (id: unknown): id is string => typeof id === 'string' && /^solution-[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id) && id.length <= 64;
export const projectPriorities = (p: ElinaProject) => [p.primaryGoal, p.secondaryGoal, p.tertiaryGoal].filter(Boolean);
export const projectCapabilityIds = (p: ElinaProject) => [...new Set(p.solutions.length ? p.solutions.filter(s => s.status === 'retained').flatMap(s => s.capabilityIds) : p.benefits.map(b => b.capabilityId))].sort();
export const proposalVersion = (p: ElinaProject): 1 | 2 => p.solutions.length ? 2 : 1;

export function toggleRankedChoice(selected: string[], choice: string): string[] {
  return selected.includes(choice) ? selected.filter(s => s !== choice) : selected.length < 3 ? [...selected, choice] : selected;
}
export function rankedChoiceMessage(selected: string[], detail: string, heading = 'Mes priorités, dans cet ordre :'): string {
  if (selected.length > 3 || new Set(selected).size !== selected.length || selected.some(s => !s.trim() || s.length > 100)) throw new Error('INVALID_PRIORITIES');
  const text = [selected.length ? heading + '\n' + selected.map((s, i) => `${i + 1}. ${s}`).join('\n') : '', detail.trim()].filter(Boolean).join('\n\n');
  if (text.length > 1500) throw new Error('MESSAGE_TOO_LONG');
  return text;
}

const record = (v: unknown): Record<string, unknown> => {
  if (!v || typeof v !== 'object' || Array.isArray(v)) throw new Error('INVALID_PROJECT');
  return v as Record<string, unknown>;
};
const field = (v: unknown, max: number, required = false): string => {
  if (typeof v !== 'string' || v.length > max || (required && !v.trim()) || /[\u0000-\u0008\u000B\u000C\u000E-\u001F]/.test(v)) throw new Error('INVALID_PROJECT');
  return v.trim();
};
const list = (v: unknown, max: number): unknown[] => {
  if (!Array.isArray(v) || v.length > max) throw new Error('INVALID_PROJECT');
  return v;
};
export function validateElinaProject(value: unknown, complete = false): ElinaProject {
  const p = record(value);
  const solutions = list(p.solutions ?? [], 6).map(value => {
    const s = record(value);
    if (!isSolutionId(s.id) || !['explore', 'retained'].includes(String(s.status)) || typeof s.usesAi !== 'boolean') throw new Error('INVALID_SOLUTION');
    const capabilityIds = list(s.capabilityIds, 6).map(id => {
      if (typeof id !== 'string' || !CAPABILITIES.some(c => c.id === id)) throw new Error('INVALID_CAPABILITY');
      return id;
    });
    return { id: s.id, title: field(s.title, 90, true), description: field(s.description, 280, true), value: field(s.value, 200, true), status: s.status as ProjectSolution['status'], usesAi: s.usesAi, capabilityIds: [...new Set(capabilityIds)] };
  });
  if (new Set(solutions.map(s => s.id)).size !== solutions.length) throw new Error('INVALID_SOLUTION');
  const benefits = list(p.benefits, 6).map(value => {
    const b = record(value);
    if (typeof b.capabilityId !== 'string' || !CAPABILITIES.some(c => c.id === b.capabilityId)) throw new Error('INVALID_CAPABILITY');
    return { capabilityId: b.capabilityId, title: field(b.title, 90, true), description: field(b.description, 280, true) };
  });
  if (new Set(benefits.map(b => b.capabilityId)).size !== benefits.length || (complete && !benefits.length && !solutions.length)) throw new Error('INVALID_CAPABILITY');
  if (solutions.length && benefits.length) throw new Error('MIXED_PROPOSAL_FORMAT');
  const journey = list(p.journey, 4).map(value => {
    const s = record(value);
    return { title: field(s.title, 90, true), description: field(s.description, 280, true) };
  });
  return {
    title: field(p.title, 100, complete), situation: field(p.situation, 600, complete),
    primaryGoal: field(p.primaryGoal, 240, complete), secondaryGoal: field(p.secondaryGoal, 240),
    tertiaryGoal: field(p.tertiaryGoal ?? '', 240), ambition: field(p.ambition ?? '', 400), ecosystem: field(p.ecosystem ?? '', 600), solutions,
    recommendation: field(p.recommendation ?? '', 240),
    businessBenefits: list(p.businessBenefits ?? [], 3).map(value => { const b = record(value); return { title: field(b.title, 70, true), description: field(b.description, 180, true) }; }),
    activity: field(p.activity, 300), team: field(p.team, 100), tools: field(p.tools, 300), timing: field(p.timing, 100),
    benefits, journey, assumptions: list(p.assumptions, 4).map(s => field(s, 200, true)),
  };
}
export function validateProposal(value: unknown, selected?: string[]): ProjectProposal {
  const p = record(value);
  if (![1, 2].includes(Number(p.version)) || p.source !== 'elina') throw new Error('INVALID_PROPOSAL');
  const project = validateElinaProject(p, true);
  if (p.version !== proposalVersion(project) || project.solutions.some(s => s.status !== 'retained')) throw new Error('INVALID_PROPOSAL');
  const expected = projectCapabilityIds(project);
  if (selected && (selected.length !== expected.length || expected.some(id => !selected.includes(id)))) throw new Error('SCOPE_MISMATCH');
  return { version: p.version as 1 | 2, source: 'elina', ...project };
}
export function validateElinaReply(value: unknown): ElinaReply {
  const r = record(value);
  if (typeof r.ready !== 'boolean') throw new Error('INVALID_REPLY');
  const choiceMode = r.choiceMode ?? 'single';
  if (!['single', 'ranked'].includes(String(choiceMode))) throw new Error('INVALID_REPLY');
  const result = {
    heading: field(r.heading, 80, true), message: field(r.message, 480, true),
    question: field(r.question, 180), choices: list(r.choices, choiceMode === 'ranked' ? 6 : 3).map(s => field(s, 100, true)), choiceMode: choiceMode as ElinaReply['choiceMode'],
    ready: r.ready, project: validateElinaProject(r.project, r.ready),
  };
  // A response cannot become a questionnaire or include invented monetary amounts.
  if ((result.question.match(/\?/g) || []).length > 1 || result.message.includes('?') || (result.ready && (result.question || result.choices.length)) || /[€$£]|\b\d[\d\s.,]*\s*(?:euros?|USD|EUR)\b/i.test(JSON.stringify(result))) throw new Error('INVALID_REPLY');
  if (new Set(result.choices).size !== result.choices.length || (result.ready && result.project.solutions.some(s => s.status !== 'retained'))) throw new Error('INVALID_REPLY');
  return { ...result, ...(r.references !== undefined ? { references: validateReferences(r.references) } : {}) };
}
const string = (maxLength: number) => ({ type: 'string', maxLength });
const object = (properties: Record<string, unknown>) => ({ type: 'object', properties, required: Object.keys(properties), additionalProperties: false });
export const ELINA_RESPONSE_SCHEMA = object({
  heading: string(80), message: string(480), question: string(180),
  choices: { type: 'array', maxItems: 6, items: string(100) }, choiceMode: { type: 'string', enum: ['single', 'ranked'] }, ready: { type: 'boolean' },
  project: object({
    title: string(100), situation: string(600), primaryGoal: string(240), secondaryGoal: string(240), tertiaryGoal: string(240), ambition: string(400), ecosystem: string(600),
    recommendation: string(240),
    businessBenefits: { type: 'array', maxItems: 3, items: object({ title: string(70), description: string(180) }) },
    activity: string(300), team: string(100), tools: string(300), timing: string(100),
    benefits: { type: 'array', maxItems: 6, items: object({ capabilityId: { type: 'string', enum: CAPABILITIES.map(c => c.id) }, title: string(90), description: string(280) }) },
    solutions: { type: 'array', maxItems: 6, items: object({ id: string(64), title: string(90), description: string(280), value: string(200), status: { type: 'string', enum: ['explore', 'retained'] }, usesAi: { type: 'boolean' }, capabilityIds: { type: 'array', maxItems: 6, items: { type: 'string', enum: CAPABILITIES.map(c => c.id) } } }) },
    journey: { type: 'array', maxItems: 4, items: object({ title: string(90), description: string(280) }) },
    assumptions: { type: 'array', maxItems: 4, items: string(200) },
  }),
});
export function proposalText(p: ElinaProject): string {
  return [proposalAmbition(p), ...(p.recommendation ? ['', 'La réponse RDV-Partners', p.recommendation] : []),
    ...proposalSections(p).flatMap(s => ['', `${Number(s.number)}. ${s.title}`, ...s.cards.flatMap(c => [c.title || '', c.text, ...(c.value ? [`Bénéfice direct : ${c.value}`] : [])]), ...(s.note ? [s.note] : [])]),
  ].join('\n');
}

